// Lib
import React, { useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Popover, UtilityButton } from '@milanote/roma';
import { cx } from '@milanote/roma/utils';
import { useTranslator } from '../../../../components/i18n/useTranslator';

// Utils
import { getStrokeColor } from '../drawingEditorUtils';
import { stopDefaultAndPropagation } from '../../../../utils/domUtil';
import { setDrawingEditorColorToolTempColorStyles } from './DrawingEditorColorTool';

// Components
import CustomColorInput from '../../../../workspace/toolbar/components/selectionTools/colorTools/CustomColorInput';
import ColorPopupContent from '../../../../workspace/toolbar/components/selectionTools/colorTools/popup/ColorPopupContent';
import ColorToolIcon from '../../../../workspace/toolbar/components/selectionTools/colorTools/ColorToolIcon';

// Selectors
import currentBoardSuggestedColorsSelector from '../../../../workspace/toolbar/components/selectionTools/colorTools/currentBoardSuggestedColorsSelector';

// Hooks
import useTouchBlockedPointerEvent from './useTouchBlockedPointerEvent';

type CustomColorInputRef = {
    open: () => void;
};

type DrawingEditorColorToolV2Props = {
    isPenMode: boolean;
    disabled: boolean;
    keepPopupOpen: boolean;
    strokeColor: string;
    defaultColors: string[];
    colorPopupOpen: boolean;
    setStrokeColor: (color: string) => void;
    setColorPopupOpen: (isOpen: boolean) => void;
    setCustomColorInputOpen: (isOpen: boolean) => void;
};

/**
 * Roma-styled drawing colour picker trigger for the V2 drawing toolbar.
 */
const DrawingEditorColorToolV2: React.FC<DrawingEditorColorToolV2Props> = ({
    isPenMode,
    disabled,
    keepPopupOpen,
    strokeColor,
    defaultColors,
    colorPopupOpen,

    setStrokeColor,
    setColorPopupOpen,
    setCustomColorInputOpen,
}) => {
    const triggerRef = useRef<HTMLButtonElement>(null);
    const customColorInputRef = useRef<CustomColorInputRef | null>(null);
    const suggestedColors = useSelector(currentBoardSuggestedColorsSelector);
    const translate = useTranslator('popup', 'drawing');

    const togglePopup = useCallback(() => setColorPopupOpen(!colorPopupOpen), [setColorPopupOpen, colorPopupOpen]);
    const onButtonPointerUp = useTouchBlockedPointerEvent(isPenMode, togglePopup);

    const closePopup = () => setColorPopupOpen(false);

    const openCustomColorPicker = useCallback(() => {
        customColorInputRef.current?.open();
    }, []);

    const setColorAndClose = (color: string) => {
        setStrokeColor(color);
        !keepPopupOpen && closePopup();
    };

    return (
        <div onDoubleClick={stopDefaultAndPropagation}>
            <UtilityButton
                ref={triggerRef}
                isDisabled={disabled}
                className="DrawingEditorColorButton"
                onPointerUp={!disabled ? onButtonPointerUp : undefined}
                onDoubleClick={stopDefaultAndPropagation}
                aria-label={translate('tools.color')}
            >
                <ColorToolIcon
                    color={getStrokeColor(strokeColor) ?? null}
                    isActive={colorPopupOpen}
                    className="square !bg-transparent"
                />
            </UtilityButton>

            <CustomColorInput
                className={cx('size-0', colorPopupOpen && 'is-open')}
                ref={customColorInputRef}
                onFocus={() => setCustomColorInputOpen(true)}
                onBlur={() => setCustomColorInputOpen(false)}
                initialColor={getStrokeColor(strokeColor)}
                setTempColor={setDrawingEditorColorToolTempColorStyles}
                setColor={setColorAndClose}
            />

            <Popover
                triggerRef={triggerRef}
                isOpen={colorPopupOpen}
                onOpenChange={setColorPopupOpen}
                placement="top"
                popoverClassName="DrawingEditorColorPopup color-popup"
            >
                <ColorPopupContent
                    defaultColors={defaultColors}
                    suggestedColors={suggestedColors}
                    selectedColor={strokeColor}
                    closePopup={closePopup}
                    openCustomColorPicker={openCustomColorPicker}
                    setColor={setColorAndClose}
                    customColorInputRef={customColorInputRef}
                />
            </Popover>
        </div>
    );
};

export default DrawingEditorColorToolV2;
